package com.gastoncantera.intercom.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.basicMarquee
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.gastoncantera.intercom.call.CallManager
import com.gastoncantera.intercom.call.CallState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

private val AcceptGreen = Color(0xFF34C759)
private val CallingOrange = Color(0xFFFF9500)
private val RejectRed = Color(0xFFFF3B30)

/**
 * UI compartida de una llamada activa. El flujo/estados ya están en [CallManager];
 * esto solo renderiza según [CallState] y dispara las acciones.
 */

/** Lado caller (portero): llamando / en llamada, con auto-preview de la cámara local. */
@OptIn(ExperimentalFoundationApi::class)
@Composable
fun CallerActiveCall(
    title: String,
    callManager: CallManager,
    callState: CallState,
    scope: CoroutineScope,
    modifier: Modifier = Modifier,
) {
    val localVideo by callManager.localVideoTrack.collectAsState()
    val remoteAudio by callManager.remoteAudioTrack.collectAsState()

    RemoteAudioSink(remoteAudio)
    OnAppBackground { scope.launch { callManager.onAppBackground() } }

    Column(
        modifier = modifier.fillMaxSize()
            .windowInsetsPadding(WindowInsets.safeDrawing)
            .padding(24.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        val statusText =
            if (callState == CallState.Calling) "Llamando a $title…"
            else "En llamada con $title"
        Text(
            text = if (callState == CallState.Connected) {
                "$statusText        $statusText"
            } else {
                statusText
            },
            modifier = if (callState == CallState.Connected) {
                Modifier
                    .widthIn(max = 320.dp)
                    .fillMaxWidth()
                    .basicMarquee(
                        iterations = Int.MAX_VALUE,
                        repeatDelayMillis = 0,
                    )
            } else {
                Modifier
            },
            color = when (callState) {
                CallState.Calling -> CallingOrange
                CallState.Connected -> AcceptGreen
                else -> Color.Unspecified
            },
            style = MaterialTheme.typography.titleMedium,
            maxLines = 1,
        )

        localVideo?.let { track ->
            Video(
                videoTrack = track,
                modifier = Modifier.fillMaxWidth().weight(1f).padding(top = 16.dp),
            )
        }

        HangupButton(callManager, scope)
    }
}

/** Lado callee (usuario): entrante (contestar/rechazar), conectado (video remoto), atendido en otro device. */
@Composable
fun CalleeActiveCall(
    title: String,
    callManager: CallManager,
    callState: CallState,
    scope: CoroutineScope,
    modifier: Modifier = Modifier,
    subtitle: String = "",
) {
    val remoteVideo by callManager.remoteVideoTrack.collectAsState()
    val remoteAudio by callManager.remoteAudioTrack.collectAsState()

    RemoteAudioSink(remoteAudio)
    Ringtone(playing = callState == CallState.Incoming)
    OnAppBackground { scope.launch { callManager.onAppBackground() } }

    if (callState == CallState.Incoming) {
        IncomingCall(
            title = title,
            subtitle = subtitle,
            onAccept = { scope.launch { callManager.accept() } },
            onReject = { scope.launch { callManager.reject() } },
            modifier = modifier,
        )
        return
    }

    Column(
        modifier = modifier.fillMaxSize()
            .windowInsetsPadding(WindowInsets.safeDrawing)
            .padding(24.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        when (callState) {
            CallState.Connected -> {
                Text(title, style = MaterialTheme.typography.titleMedium, textAlign = TextAlign.Center)
                if (subtitle.isNotBlank()) {
                    Text(subtitle, style = MaterialTheme.typography.bodyMedium)
                }
                remoteVideo?.let { track ->
                    Video(
                        videoTrack = track,
                        modifier = Modifier.fillMaxWidth().weight(1f).padding(top = 16.dp),
                    )
                }
                HangupButton(callManager, scope)
            }

            CallState.AnsweredElsewhere -> {
                Text(
                    "Atendido en otro dispositivo",
                    style = MaterialTheme.typography.titleMedium,
                    textAlign = TextAlign.Center,
                )
            }

            else -> {}
        }
    }
}

/** Pantalla de llamada entrante estilo teléfono: ubicación arriba, acciones (rechazar/aceptar) abajo. */
@Composable
private fun IncomingCall(
    title: String,
    subtitle: String,
    onAccept: () -> Unit,
    onReject: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxSize()
            .windowInsetsPadding(WindowInsets.safeDrawing)
            .padding(horizontal = 24.dp, vertical = 32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.weight(1f))

        Text(
            "Llamada entrante",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            title,
            style = MaterialTheme.typography.titleMedium,
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(top = 12.dp),
        )
        if (subtitle.isNotBlank()) {
            Text(
                subtitle,
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(top = 4.dp),
            )
        }

        Spacer(Modifier.weight(2f))

        Row(
            modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp),
            horizontalArrangement = Arrangement.SpaceEvenly,
        ) {
            CallActionButton(color = RejectRed, glyph = "✕", label = "Rechazar", onClick = onReject)
            CallActionButton(color = AcceptGreen, glyph = "☎", label = "Aceptar", onClick = onAccept)
        }
    }
}

@Composable
private fun CallActionButton(
    color: Color,
    glyph: String,
    label: String,
    onClick: () -> Unit,
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Box(
            modifier = Modifier.size(72.dp).clip(CircleShape).background(color).clickable(onClick = onClick),
            contentAlignment = Alignment.Center,
        ) {
            Text(glyph, color = Color.White, style = MaterialTheme.typography.headlineSmall)
        }
        Text(label, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.padding(top = 8.dp))
    }
}

@Composable
fun HangupButton(callManager: CallManager, scope: CoroutineScope) {
    Button(
        onClick = { scope.launch { callManager.hangup() } },
        modifier = Modifier.padding(top = 16.dp),
    ) {
        Text("Colgar")
    }
}
